import React from 'react';
import { Card } from '@/components/ui/card';
import { Scale } from 'lucide-react';

export default function TermsOfService() {
  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      <Card className="p-8 glass-card">
        <div className="flex items-center gap-3 mb-6">
          <Scale className="h-8 w-8 text-primary" />
          <h1 className="text-4xl font-bold">Terms of Service</h1>
        </div>

        <div className="space-y-6 text-muted-foreground">
          <p className="text-sm">Last Updated: March 24, 2026</p>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing and using PeptideForge, you agree to be bound by these Terms of Service and all applicable laws. 
              If you disagree with any part of these terms, you may not use our service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">2. Research Use Only</h2>
            <p>
              PeptideForge is designed exclusively for laboratory research and educational purposes. By using this service, you acknowledge that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>All information is for research purposes only</li>
              <li>No content constitutes medical advice</li>
              <li>Research peptides are not for human consumption</li>
              <li>You are 18 years of age or older</li>
              <li>You comply with all local, state, and federal laws</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">3. Account Responsibilities</h2>
            <p>You are responsible for:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Maintaining the security of your account credentials</li>
              <li>All activity under your account</li>
              <li>Providing accurate and current information</li>
              <li>Notifying us of unauthorized access</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">4. Subscription Plans</h2>
            <p className="mb-2"><strong>Free Plan:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Access to 20+ pre-loaded peptides</li>
              <li>1 custom peptide</li>
              <li>Basic features</li>
            </ul>
            <p className="mt-3 mb-2"><strong>Premium Plans:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Monthly: £4.99/month</li>
              <li>Annual: £50/year</li>
              <li>Unlimited custom peptides</li>
              <li>Advanced analytics</li>
              <li>Data export</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">5. Payment & Billing</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Payments processed securely through Stripe</li>
              <li>Subscriptions renew automatically unless canceled</li>
              <li>Cancel anytime from settings</li>
              <li>No refunds for partial subscription periods</li>
              <li>Prices subject to change with 30 days notice</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">6. Prohibited Uses</h2>
            <p>You may NOT:</p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Use the service for illegal purposes</li>
              <li>Share your account with others</li>
              <li>Attempt to hack or disrupt the service</li>
              <li>Scrape or extract data without permission</li>
              <li>Misrepresent yourself or your affiliation</li>
              <li>Use information for medical treatment decisions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">7. Intellectual Property</h2>
            <p>
              All content, features, and functionality are owned by PeptideForge and protected by international copyright, trademark, 
              and intellectual property laws. Your data remains yours, but we retain rights to the platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">8. Disclaimer of Warranties</h2>
            <p>
              PeptideForge is provided "AS IS" without warranties of any kind. We do not guarantee:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Uninterrupted or error-free operation</li>
              <li>Accuracy of calculations or information</li>
              <li>Fitness for any particular purpose</li>
              <li>That defects will be corrected</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">9. Limitation of Liability</h2>
            <p>
              PeptideForge, its developers, and affiliates shall not be liable for any indirect, incidental, special, consequential, 
              or punitive damages resulting from your use or inability to use the service. Our total liability shall not exceed the amount 
              you paid in the last 12 months.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">10. Data Backup</h2>
            <p>
              While we perform regular backups, you are responsible for maintaining your own backups of critical data. 
              Export your data regularly using the CSV export feature.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">11. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your account for:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Violation of these terms</li>
              <li>Illegal activity</li>
              <li>Non-payment</li>
              <li>Abusive behavior</li>
            </ul>
            <p className="mt-3">
              You may terminate your account anytime from settings. Upon termination, your data will be deleted per our retention policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">12. Changes to Terms</h2>
            <p>
              We may modify these terms at any time. Significant changes will be notified via email. Continued use after changes 
              constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">13. Governing Law</h2>
            <p>
              These terms are governed by the laws of the United Kingdom. Disputes shall be resolved in UK courts.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-3">14. Contact Information</h2>
            <p>For questions about these terms:</p>
            <ul className="list-none space-y-1 ml-4 mt-2">
              <li>Email: legal@peptideforge.com</li>
              <li>Support: support@peptideforge.com</li>
            </ul>
          </section>

          <div className="p-4 rounded-lg bg-primary/10 border border-primary mt-8">
            <p className="text-primary font-medium">
              By using PeptideForge, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}